import store, { RootState } from '@/store'
import { CartLineInput, CartLineUpdateInput } from '@/types/storefront.types'
import {
  addLines,
  createCheckoutCart,
  getCheckoutCart,
  removeLines,
  updateDate,
  updateLinesDebounced,
} from '@/utils/checkout'
import {
  getBase64Id,
  getISODate,
  isBeforeToday,
  removeFromArray,
} from '@/utils/general-utils'
import { Cart, CartLine } from '@/utils/shopify'
import { cloneDeep } from 'lodash-es'
import { ActionTree, GetterTree, MutationTree } from 'vuex'
import {
  REMOVE_LINE,
  SET_CHECKOUT_CART,
  SET_CHECKOUT_STEP,
  SET_CHECKOUT_SUB_STEP,
  SET_QUERY,
  UPDATE_LINE,
} from '../types'

export interface CheckoutState {
  checkoutCart: Cart | null;
  isLoading: boolean;
  isUpdatingSubtotal: boolean;
  step: number;
  subStep: number;
  paymentWindow: Window | null;
  query: { [key: string]: string };
}

const state: CheckoutState = {
  checkoutCart: null,
  isLoading: true,
  isUpdatingSubtotal: true,
  step: 1,
  subStep: 1,
  paymentWindow: null,
  query: {},
}

const getters: GetterTree<CheckoutState, RootState> = {
  cartLineCount: (state) => state.checkoutCart?.lines.edges.length,
  cartLines: (state) =>
    state.checkoutCart?.lines.edges.map((item) => item.node),
  subtotal: (state) => state.checkoutCart?.cost.subtotalAmount.amount,
  checkoutStep: (state) => state.step,
  checkoutSubStep: (state) => state.subStep,
  dateOfVisit: (state) =>
    state.checkoutCart?.attributes.find((attr) => attr.key === 'dateOfVisit')
      ?.value,
  startTime: (state) => {
    const start = state.checkoutCart?.lines.edges[0]?.node?.attributes?.find(
      (attr) => attr.key === 'start'
    )?.value
    return `${start?.slice(0, 2)}:${start?.slice(2, 4)}`
  },
  endTime: (state) => {
    const end = state.checkoutCart?.lines.edges[0]?.node?.attributes?.find(
      (attr) => attr.key === 'end'
    )?.value
    return `${end?.slice(0, 2)}:${end?.slice(2, 4)}`
  },
  isCartLoading: (state) => state.isLoading,
  isUpdatingSubtotal: (state) => state.isUpdatingSubtotal,
  checkoutCartId: (state) => state.checkoutCart?.id,
  checkoutCart: (state) => state.checkoutCart,
  paymentWindow: (state) => state.paymentWindow,
  holdBookingId: (state, getters) => {
    let id = ''
    if (getters.cartLines) {
      for (const line of getters.cartLines as CartLine[]) {
        const customAttributes = line.attributes
        for (const holdBookingId of customAttributes) {
          if (holdBookingId.key === 'holdBookingId' && holdBookingId.value) {
            id = holdBookingId.value
            break
          }
        }
        if (id) break
      }
    }
    return id
  },
  query: (state) => state.query,
}

const mutations: MutationTree<CheckoutState> = {
  [SET_CHECKOUT_CART](state, checkoutCart: Cart) {
    const newCheckoutCart = cloneDeep(checkoutCart)
    newCheckoutCart.id = getBase64Id(newCheckoutCart.id)
    newCheckoutCart.lines.edges = newCheckoutCart.lines.edges.map((edge) => {
      const line = edge.node
      line.id = getBase64Id(line.id)
      line.merchandise.id = getBase64Id(line.merchandise.id)
      line.merchandise.product.id = getBase64Id(line.merchandise.product.id)
      return { node: line }
    })
    state.checkoutCart = newCheckoutCart

    if (newCheckoutCart) {
      state.isLoading = false
      state.isUpdatingSubtotal = false
    }
  },

  [UPDATE_LINE](state, line: CartLineUpdateInput) {
    const lines = state.checkoutCart?.lines.edges

    const index = lines?.findIndex((item) => item.node.id === line.id)
    if (state.checkoutCart && lines && index !== undefined && index > -1) {
      const newLines = [...lines]
      newLines[index] = {
        ...newLines[index],
        ...line,
      }
      state.checkoutCart = {
        ...state.checkoutCart,
        lines: { edges: newLines },
      }
    }
  },

  [REMOVE_LINE](state, lineId: string) {
    const lines = state.checkoutCart?.lines.edges

    if (state.checkoutCart && lines) {
      const newLines = removeFromArray(
        (item) => item.node.id === lineId,
        lines
      )
      state.checkoutCart = {
        ...state.checkoutCart,
        lines: { edges: newLines },
      }
    }
  },

  [SET_CHECKOUT_STEP](state, step) {
    state.step = step
    state.subStep = 1
  },

  [SET_CHECKOUT_SUB_STEP](state, subStep) {
    state.subStep = subStep
  },

  [SET_QUERY](state, query: { [key: string]: string }) {
    state.query = query
  },
}

const actions: ActionTree<CheckoutState, RootState> = {
  async loadCheckoutCart({ commit }) {
    const checkoutCartId = localStorage.getItem(
      `vc_ticket_${store.state.auth.merchant}_checkoutCartId`
    )

    if (checkoutCartId) {
      try {
        let checkoutCart = await getCheckoutCart(checkoutCartId)

        if (checkoutCart.lines.edges.length === 0) {
          checkoutCart = await createCheckoutCart()
        }

        // Remove all invalid line items
        const invalidLines = checkoutCart.lines.edges.filter(
          (line) => !line.node.merchandise
        )
        const invalidLineIds = invalidLines.map((line) => line.node.id)
        await removeLines(checkoutCartId, invalidLineIds)
        const newCheckoutCart = cloneDeep(checkoutCart)
        newCheckoutCart.lines.edges = checkoutCart.lines.edges.filter(
          (line) => !!line.node.merchandise
        )

        commit(SET_CHECKOUT_CART, newCheckoutCart)

        const dateOfVisit = checkoutCart?.attributes?.find(
          (attr) => attr.key === 'dateOfVisit'
        )?.value
        if (dateOfVisit && isBeforeToday(dateOfVisit)) {
          const prevAttributes = checkoutCart.attributes
          const newDateOfVisit =
            new Date().getDay() === 1
              ? new Date(Date.now() + 24 * 60 * 60 * 1000)
              : new Date()
          const checkoutCartWithUpdatedDate = await updateDate(
            checkoutCart.id,
            // set visit date to today or tomorrow if today is monday
            getISODate(newDateOfVisit),
            prevAttributes
          )
          commit(SET_CHECKOUT_CART, checkoutCartWithUpdatedDate)
          return
        }

        commit(SET_CHECKOUT_CART, checkoutCart)
      } catch (e) {
        console.log('Error', e)

        // TODO: show error message
        const checkoutCart = await createCheckoutCart()
        commit(SET_CHECKOUT_CART, checkoutCart)
      }
    } else {
      const checkoutCart = await createCheckoutCart()
      commit(SET_CHECKOUT_CART, checkoutCart)
    }
  },

  async addLines({ commit, state }, lines: CartLineInput[]) {
    if (state.checkoutCart) {
      const checkoutCart = await addLines(state.checkoutCart.id, lines)
      commit(SET_CHECKOUT_CART, checkoutCart)
    }
  },

  async updateLine({ commit, state }, line: CartLineUpdateInput) {
    if (state.checkoutCart) {
      state.isUpdatingSubtotal = true

      const originalLine = state.checkoutCart.lines.edges.find(
        (item) => item.node.id === line.id
      )?.node
      commit(UPDATE_LINE, line)

      try {
        await updateLinesDebounced(
          state.checkoutCart.id,
          [line],
          (checkoutCart) => {
            commit(SET_CHECKOUT_CART, checkoutCart)
            state.isUpdatingSubtotal = false
          }
        )
      } catch {
        // Revert UI if there is an error with api call to cart
        if (originalLine) {
          commit(UPDATE_LINE, originalLine)
        }
      }
    }
  },

  async updateLines({ commit, state }, lines: CartLineUpdateInput[]) {
    if (state.checkoutCart) {
      state.isUpdatingSubtotal = true

      const originalLines: Array<CartLine | undefined> = []

      for (const line of lines) {
        const originalLine = state.checkoutCart.lines.edges.find(
          (item) => item.node.id === line.id
        )?.node
        originalLines.push(originalLine)

        commit(UPDATE_LINE, line)
      }

      try {
        await updateLinesDebounced(
          state.checkoutCart.id,
          lines,
          (checkoutCart) => {
            commit(SET_CHECKOUT_CART, checkoutCart)
            state.isUpdatingSubtotal = false
          }
        )
      } catch {
        // Revert UI if there is an error with api call to cart
        for (const originalLine of originalLines) {
          if (originalLine) {
            commit(UPDATE_LINE, originalLine)
          }
        }
      }
    }
  },

  async removeLine({ commit, state, dispatch }, lineId: string) {
    if (state.checkoutCart) {
      state.isUpdatingSubtotal = true

      try {
        const checkoutCart = await removeLines(state.checkoutCart.id, [lineId])
        commit(SET_CHECKOUT_CART, checkoutCart)
        state.isUpdatingSubtotal = false
      } catch {
        dispatch('resetCheckoutCart')
        state.isUpdatingSubtotal = false
      }
    }
  },

  async removeLines({ commit, state, dispatch }, lineIds: string[]) {
    if (state.checkoutCart) {
      state.isUpdatingSubtotal = true

      try {
        const checkoutCart = await removeLines(state.checkoutCart.id, lineIds)
        commit(SET_CHECKOUT_CART, checkoutCart)
        state.isUpdatingSubtotal = false
      } catch {
        dispatch('resetCheckoutCart')
        state.isUpdatingSubtotal = false
      }
    }
  },

  async updateDateOfVisit({ commit, state }, newISODate: string) {
    if (state.checkoutCart) {
      const prevAttributes = state.checkoutCart.attributes
      const checkoutCart = await updateDate(
        state.checkoutCart.id,
        newISODate,
        prevAttributes
      )
      commit(SET_CHECKOUT_CART, checkoutCart)
    }
  },

  async resetCheckoutCart({ commit }) {
    try {
      const checkoutCart = await createCheckoutCart()
      commit(SET_CHECKOUT_CART, checkoutCart)
    } catch (err) {
      console.log(err)
    }
  },
}

export default {
  state,
  getters,
  actions,
  mutations,
}
